import { WidgetStyles } from '../styles/WidgetStyles';
import { SizeResolver } from '../utils/SizeResolver';
import { ExpandCollapseManager } from './ExpandCollapseManager';
import { FullscreenManager } from './FullscreenManager';
import { WidgetBoundaryController } from './WidgetBoundaryController';
import { WidgetDragController } from './WidgetDragController';
import { WidgetResizeController, } from './WidgetResizeController';
import { WidgetWindowResizeController } from './WidgetWindowResizeController';
/**
 * Delay in milliseconds to allow DOM changes to take effect before restoring CSS transitions.
 * This prevents visual glitches when programmatically resizing widgets by ensuring the
 * transition property is restored after the DOM has been updated.
 */
const TRANSITION_RESTORE_DELAY_MS = 50;
/**
 * Manages DOM interactions for widgets
 */
export class WidgetDOMManager {
    constructor(config, theme, onClose, onToggleFullscreen, onToggleExpandCollapse) {
        Object.defineProperty(this, "container", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "dragHandle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "contentContainer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "reactRoot", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "dragController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "resizeController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "boundaryController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "windowResizeController", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fullscreenManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "expandCollapseManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "animationFrameId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "styles", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onClose", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onToggleFullscreen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onToggleExpandCollapse", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isMounted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "handlers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                constrainDragPosition: () => { },
                getResizeMetrics: () => ({
                    position: { x: 0, y: 0 },
                    size: { width: 0, height: 0 },
                    viewportSize: { width: 0, height: 0 },
                }),
                applyResizeChanges: () => { },
                enforceAllBoundaries: () => { },
                getWidgetPosition: () => ({ x: 0, y: 0 }),
                getWidgetSize: () => ({ width: 0, height: 0 }),
            }
        });
        this.config = config;
        this.styles = new WidgetStyles(theme);
        this.onClose = onClose;
        this.onToggleFullscreen = onToggleFullscreen;
        this.onToggleExpandCollapse = onToggleExpandCollapse;
        this.initHandlers();
        const viewport = SizeResolver.getViewportSize();
        this.boundaryController = new WidgetBoundaryController({
            minWidth: SizeResolver.parseValue(this.config.sizeConstraints?.minWidth ?? 200, viewport.width),
            minHeight: SizeResolver.parseValue(this.config.sizeConstraints?.minHeight ?? 150, viewport.height),
            maxWidth: this.config.sizeConstraints?.maxWidth
                ? SizeResolver.parseValue(this.config.sizeConstraints.maxWidth, viewport.width)
                : undefined,
            maxHeight: this.config.sizeConstraints?.maxHeight
                ? SizeResolver.parseValue(this.config.sizeConstraints.maxHeight, viewport.height)
                : undefined,
        });
        this.dragController = new WidgetDragController({
            onDragMove: this.handlers.constrainDragPosition,
        });
        this.resizeController = new WidgetResizeController({
            getResizeContext: this.handlers.getResizeMetrics,
            applyChanges: this.handlers.applyResizeChanges,
        });
        this.windowResizeController = new WidgetWindowResizeController({
            getWidgetPosition: this.handlers.getWidgetPosition,
            getWidgetSize: this.handlers.getWidgetSize,
            enforceConstraints: this.handlers.enforceAllBoundaries,
            boundaryController: this.boundaryController,
        });
        // Prepare size constraints for fullscreen manager
        const fullscreenConstraints = {};
        if (this.config.sizeConstraints?.maxWidth) {
            fullscreenConstraints.maxWidth = SizeResolver.parseValue(this.config.sizeConstraints.maxWidth, viewport.width);
        }
        if (this.config.sizeConstraints?.maxHeight) {
            fullscreenConstraints.maxHeight = SizeResolver.parseValue(this.config.sizeConstraints.maxHeight, viewport.height);
        }
        this.fullscreenManager = new FullscreenManager((isFullscreen) => {
            if (isFullscreen) {
                this.dragController.disableDragging();
            }
            else {
                if (this.config.capabilities?.draggable !== false) {
                    this.dragController.enableDragging();
                }
            }
        }, (position, size) => {
            this.applyProgrammaticResize(position, size);
        }, fullscreenConstraints);
        this.expandCollapseManager = new ExpandCollapseManager();
        // Auto-configure expand/collapse sizes if expand/collapse is enabled
        if (this.config.capabilities?.allowExpandCollapse) {
            this.setupExpandCollapseSizing();
        }
    }
    initHandlers() {
        this.handlers = {
            constrainDragPosition: (position) => {
                if (!this.container)
                    return;
                if (this.fullscreenManager.isFullscreen()) {
                    this.container.style.left = `${position.x}px`;
                    this.container.style.top = `${position.y}px`;
                }
                else {
                    const size = this.getSize();
                    const constrainedPosition = this.boundaryController.enforceDragBoundaries(position, size);
                    this.container.style.left = `${constrainedPosition.x}px`;
                    this.container.style.top = `${constrainedPosition.y}px`;
                    // Update expand/collapse position tracking when widget is dragged
                    if (this.config.capabilities?.allowExpandCollapse) {
                        this.expandCollapseManager.setPreCollapsePosition(constrainedPosition);
                    }
                }
            },
            applyResizeChanges: (position, size) => {
                if (!this.container)
                    return;
                this.fullscreenManager.notifyManualResize();
                this.applyResizeChangesInternal(position, size);
                // Update expand/collapse position tracking when widget is resized
                if (this.config.capabilities?.allowExpandCollapse) {
                    this.expandCollapseManager.setPreCollapsePosition(position);
                }
            },
            getResizeMetrics: () => {
                return {
                    position: {
                        x: parseInt(this.container?.style.left || '0'),
                        y: parseInt(this.container?.style.top || '0'),
                    },
                    size: {
                        width: this.container?.offsetWidth || 0,
                        height: this.container?.offsetHeight || 0,
                    },
                    viewportSize: this.boundaryController.getViewportSize(),
                };
            },
            enforceAllBoundaries: (position, size) => {
                if (!this.container)
                    return;
                const result = this.boundaryController.enforceAllBoundaries(position, size);
                this.container.style.left = `${result.position.x}px`;
                this.container.style.top = `${result.position.y}px`;
                this.container.style.width = `${result.size.width}px`;
                this.container.style.height = `${result.size.height}px`;
                // Update expand/collapse position tracking when boundaries are enforced
                if (this.config.capabilities?.allowExpandCollapse) {
                    this.expandCollapseManager.setPreCollapsePosition(result.position);
                }
            },
            getWidgetPosition: () => {
                if (!this.container) {
                    return { x: 0, y: 0 };
                }
                return {
                    x: parseInt(this.container.style.left) || 0,
                    y: parseInt(this.container.style.top) || 0,
                };
            },
            getWidgetSize: () => {
                if (!this.container) {
                    return { width: 0, height: 0 };
                }
                return {
                    width: this.container.offsetWidth,
                    height: this.container.offsetHeight,
                };
            },
        };
    }
    /**
     * Internal method to apply resize changes without triggering manual resize detection
     * Used for both user resizes and programmatic resizes
     */
    applyResizeChangesInternal(position, size) {
        if (!this.container)
            return;
        if (this.fullscreenManager.isFullscreen()) {
            this.container.style.width = `${size.width}px`;
            this.container.style.height = `${size.height}px`;
            this.container.style.left = `${position.x}px`;
            this.container.style.top = `${position.y}px`;
        }
        else {
            const enforcedResult = this.boundaryController.enforceResizeBoundaries(position, size);
            this.container.style.width = `${enforcedResult.size.width}px`;
            this.container.style.height = `${enforcedResult.size.height}px`;
            this.container.style.left = `${enforcedResult.position.x}px`;
            this.container.style.top = `${enforcedResult.position.y}px`;
        }
    }
    /**
     * Applies resize changes programmatically without triggering manual resize detection
     * Used by fullscreen manager for window resize handling
     */
    applyProgrammaticResize(position, size) {
        if (!this.container)
            return;
        const originalTransition = this.container.style.transition;
        this.container.style.transition = 'none';
        this.applyResizeChangesInternal(position, size);
        setTimeout(() => {
            if (this.container) {
                this.container.style.transition = originalTransition;
            }
        }, TRANSITION_RESTORE_DELAY_MS);
    }
    createWidgetContainer() {
        const { initialPosition, initialSize } = this.config;
        const container = document.createElement('div');
        container.id = this.config.id;
        const viewport = this.boundaryController.getViewportSize();
        let resolvedSize;
        // If expand/collapse is enabled, use constraint-based sizing instead of initialSize
        if (this.config.capabilities?.allowExpandCollapse) {
            const constraints = this.config.sizeConstraints;
            // Determine size based on startCollapsed flag
            if (this.config.startCollapsed) {
                // Use minimum constraints for collapsed state
                resolvedSize = {
                    width: constraints?.minWidth
                        ? SizeResolver.parseValue(constraints.minWidth, viewport.width)
                        : 400,
                    height: constraints?.minHeight
                        ? SizeResolver.parseValue(constraints.minHeight, viewport.height)
                        : 150,
                };
            }
            else {
                // Use maximum constraints for expanded state
                resolvedSize = {
                    width: constraints?.maxWidth
                        ? SizeResolver.parseValue(constraints.maxWidth, viewport.width)
                        : constraints?.minWidth
                            ? SizeResolver.parseValue(constraints.minWidth, viewport.width)
                            : 400,
                    height: constraints?.maxHeight
                        ? SizeResolver.parseValue(constraints.maxHeight, viewport.height)
                        : 600,
                };
            }
        }
        else {
            // Standard behavior: use initialSize
            resolvedSize = SizeResolver.resolve(initialSize || { width: 300, height: 400 }, viewport);
        }
        container.style.cssText = this.styles.getContainerStyles(initialPosition || { x: 20, y: 20 }, resolvedSize);
        return container;
    }
    createDragHeader() {
        const dragHeader = document.createElement('div');
        dragHeader.id = `${this.container.id}-drag-header`;
        const isDraggable = this.config.capabilities?.draggable !== false;
        dragHeader.style.cssText = this.styles.getDragHandleStyles(isDraggable);
        const titleText = document.createElement('div');
        titleText.id = `${this.container.id}-title`;
        titleText.textContent = this.config.title || 'Widget';
        titleText.style.cssText = this.styles.getTitleStyles();
        dragHeader.appendChild(titleText);
        if (this.config.capabilities?.canToggleFullscreen) {
            const fullscreenButton = this.createFullscreenButton();
            fullscreenButton.title = 'Fullscreen';
            dragHeader.appendChild(fullscreenButton);
        }
        if (this.config.capabilities?.allowExpandCollapse) {
            const expandCollapseButton = this.createExpandCollapseButton();
            expandCollapseButton.title = 'Expand/Collapse';
            dragHeader.appendChild(expandCollapseButton);
        }
        if (this.config.capabilities?.allowClose) {
            const closeButton = document.createElement('div');
            closeButton.id = `${this.container.id}-close-button`;
            closeButton.textContent = '✕';
            closeButton.style.cssText = this.styles.getCloseButtonStyles();
            closeButton.onclick = () => {
                this.onClose();
            };
            closeButton.title = 'Close';
            dragHeader.appendChild(closeButton);
        }
        return dragHeader;
    }
    createFullscreenButton() {
        const fullscreenButton = document.createElement('div');
        fullscreenButton.id = `${this.container.id}-fullscreen-button`;
        fullscreenButton.innerHTML = '⛶';
        fullscreenButton.style.cssText = this.styles.getFullscreenButtonStyles();
        fullscreenButton.onclick = () => this.onToggleFullscreen?.();
        return fullscreenButton;
    }
    createExpandCollapseButton() {
        const expandCollapseButton = document.createElement('div');
        expandCollapseButton.id = `${this.container.id}-expand-collapse-button`;
        // Use intuitive expand/collapse icons with diagonal expand arrow
        const updateButtonIcon = () => {
            const isExpanded = this.expandCollapseManager.isExpanded();
            expandCollapseButton.innerHTML = isExpanded ? '−' : '⇱'; // Minus when expanded (can collapse), diagonal arrows when collapsed (can expand)
            expandCollapseButton.title = isExpanded
                ? 'Collapse to compact view'
                : 'Expand to full view';
        };
        // Set initial state
        updateButtonIcon();
        // Register callback for external expand/collapse events
        this.expandCollapseManager.setStateChangeCallback(() => {
            // Small delay to ensure state is fully updated
            setTimeout(() => {
                updateButtonIcon();
            }, 10);
        });
        // Improved styling for better alignment with close button - bigger and bolder
        const baseStyles = this.styles.getCloseButtonStyles();
        expandCollapseButton.style.cssText = `${baseStyles}; display: flex; align-items: center; justify-content: center; line-height: 1; font-size: 18px; font-weight: bold;`;
        expandCollapseButton.onclick = async () => {
            if (this.onToggleExpandCollapse) {
                await this.onToggleExpandCollapse();
                // Icon will be updated automatically via state change listener
            }
        };
        return expandCollapseButton;
    }
    createContentContainer() {
        const contentContainer = document.createElement('div');
        contentContainer.id = `${this.container.id}-content`;
        contentContainer.style.cssText = this.styles.getContentStyles();
        return contentContainer;
    }
    createReactRoot() {
        const reactRoot = document.createElement('div');
        reactRoot.id = `${this.container.id}-react-root`;
        reactRoot.style.cssText = this.styles.getReactRootStyles();
        return reactRoot;
    }
    initializeDragController() {
        if (!this.dragHandle || !this.container)
            return;
        this.dragController.initialize(this.container, this.dragHandle);
    }
    initializeResizeController() {
        if (!this.container)
            return;
        this.resizeController.initialize(this.container, (direction) => this.styles.getInvisibleResizeHandleStyles(direction));
    }
    getPosition() {
        return this.handlers.getWidgetPosition();
    }
    getSize() {
        return this.handlers.getWidgetSize();
    }
    setPosition(position) {
        if (this.container) {
            const { x, y } = position;
            this.container.style.left = `${x}px`;
            this.container.style.top = `${y}px`;
            const currentPosition = this.handlers.getWidgetPosition();
            const currentSize = this.handlers.getWidgetSize();
            this.handlers.enforceAllBoundaries(currentPosition, currentSize);
        }
        return this;
    }
    setSize(size) {
        if (this.container) {
            const { width, height } = size;
            this.container.style.width = `${width}px`;
            this.container.style.height = `${height}px`;
        }
        return this;
    }
    mount() {
        if (this.isMounted)
            return;
        this.container = this.createWidgetContainer();
        this.dragHandle = this.createDragHeader();
        this.container.appendChild(this.dragHandle);
        this.contentContainer = this.createContentContainer();
        this.container.appendChild(this.contentContainer);
        this.reactRoot = this.createReactRoot();
        this.contentContainer.appendChild(this.reactRoot);
        if (this.config.capabilities?.draggable !== false) {
            this.initializeDragController();
        }
        if (this.config.capabilities?.resizable !== false) {
            this.initializeResizeController();
        }
        this.windowResizeController.initialize();
        // Initialize fullscreen manager with container
        this.fullscreenManager.setContainer(this.container);
        // Initialize expand/collapse manager with containers
        this.expandCollapseManager.setContainers(this.container, this.contentContainer);
        // Set up boundary constraint function for expand/collapse
        if (this.config.capabilities?.allowExpandCollapse) {
            this.expandCollapseManager.setBoundaryConstraintFunction((position, size) => {
                return this.boundaryController.constrainPosition(position, size);
            });
        }
        // If widget should start collapsed, ensure visual state matches internal state
        if (this.config.capabilities?.allowExpandCollapse &&
            this.config.startCollapsed) {
            // Apply collapsed styling to match the initial visual size
            this.applyInitialCollapsedState();
        }
        document.body.appendChild(this.container);
        this.boundaryController.updateViewportDimensions();
        this.isMounted = true;
    }
    unmount() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        this.dragController.cleanup();
        this.resizeController.cleanup();
        this.windowResizeController.cleanup();
        this.fullscreenManager.destroy();
        this.expandCollapseManager.destroy();
        if (this.dragHandle) {
            this.dragHandle.replaceWith(this.dragHandle.cloneNode(false));
            this.dragHandle = null;
        }
        if (this.reactRoot) {
            this.reactRoot.innerHTML = '';
            this.reactRoot = null;
        }
        if (this.contentContainer) {
            this.contentContainer.innerHTML = '';
            this.contentContainer = null;
        }
        if (this.container && this.container.parentNode) {
            document.body.removeChild(this.container);
            this.container.innerHTML = '';
            this.container = null;
        }
        this.isMounted = false;
    }
    show() {
        if (this.container) {
            this.container.style.display = 'flex';
        }
    }
    hide() {
        if (this.container) {
            this.expandCollapseManager.reset();
            this.container.style.display = 'none';
        }
    }
    getContainer() {
        return this.container;
    }
    getReactRoot() {
        return this.reactRoot;
    }
    getContainerId() {
        return this.container ? this.container.id : '';
    }
    /**
     * Enters fullscreen mode
     * @param skipTransition - If true, applies fullscreen styles immediately without transition
     */
    async enterFullscreen(skipTransition = false) {
        return this.fullscreenManager.enterFullscreen(skipTransition);
    }
    /**
     * Exits fullscreen mode
     */
    async exitFullscreen() {
        return this.fullscreenManager.exitFullscreen();
    }
    /**
     * Toggles fullscreen mode
     */
    async toggleFullscreen() {
        return this.fullscreenManager.toggleFullscreen();
    }
    /**
     * Checks if widget is in fullscreen mode
     */
    isFullscreen() {
        return this.fullscreenManager.isFullscreen();
    }
    /**
     * Collapses the widget to compact mode
     * @returns Promise that resolves to true if successful, false otherwise
     */
    async collapse() {
        return this.expandCollapseManager.collapse();
    }
    /**
     * Expands the widget to full mode
     * @returns Promise that resolves to true if successful, false otherwise
     */
    async expand() {
        return this.expandCollapseManager.expand();
    }
    /**
     * Toggles expand/collapse state
     * @returns Promise that resolves to true if successful, false otherwise
     */
    async toggleExpandCollapse() {
        return this.expandCollapseManager.toggleExpandCollapse();
    }
    /**
     * Checks if the widget is currently expanded
     * @returns True if the widget is expanded
     */
    isExpanded() {
        return this.expandCollapseManager.isExpanded();
    }
    /**
     * Sets custom sizes for expand/collapse modes
     * @param compactSize Size when collapsed
     * @param expandedSize Size when expanded
     */
    setExpandCollapseSizes(compactSize, expandedSize) {
        this.expandCollapseManager.setSizes(compactSize, expandedSize);
    }
    /**
     * Auto-configures expand/collapse sizing based on size constraints
     * @private
     */
    setupExpandCollapseSizing() {
        const constraints = this.config.sizeConstraints;
        const viewport = this.boundaryController.getViewportSize();
        // Derive compact size from minimum constraints
        const compactSize = {
            width: constraints?.minWidth
                ? SizeResolver.parseValue(constraints.minWidth, viewport.width)
                : 400,
            height: constraints?.minHeight
                ? SizeResolver.parseValue(constraints.minHeight, viewport.height)
                : 150,
        };
        // Derive expanded size from maximum constraints or use reasonable defaults
        const expandedSize = {
            width: constraints?.maxWidth
                ? SizeResolver.parseValue(constraints.maxWidth, viewport.width)
                : compactSize.width, // Same width if no max specified
            height: constraints?.maxHeight
                ? SizeResolver.parseValue(constraints.maxHeight, viewport.height)
                : 600, // Default expanded height
        };
        // Set the computed sizes
        this.expandCollapseManager.setSizes(compactSize, expandedSize);
        // Set initial state based on startCollapsed flag
        const shouldStartExpanded = !this.config.startCollapsed;
        this.expandCollapseManager.setInitialState(shouldStartExpanded);
    }
    /**
     * Applies initial collapsed styling when widget starts in collapsed state
     * @private
     */
    applyInitialCollapsedState() {
        if (!this.container)
            return;
        // Store initial position for later expansion
        const preCollapsePosition = {
            x: parseInt(this.container.style.left) || 0,
            y: parseInt(this.container.style.top) || 0,
        };
        // Set the stored position in the manager
        this.expandCollapseManager.setPreCollapsePosition(preCollapsePosition);
        // Get the compact size from the expand/collapse manager
        const compactSize = this.expandCollapseManager.getCompactSize();
        // Actually resize the widget to the compact size
        this.container.style.width = `${compactSize.width}px`;
        this.container.style.height = `${compactSize.height}px`;
        // Add collapsed class for CSS styling
        this.container.classList.add('widget-collapsed');
        this.container.classList.remove('widget-expanded');
    }
}
//# sourceMappingURL=WidgetDOMManager.js.map